/**
 * Unix Time Command
 * Convert unix timestamps to/from human-readable dates
 */
import { Command } from 'commander';
import { UtilsService } from '../../services/utils.service.js';

async function handleUnixTime(
    input: string | undefined,
    options: {
        format: 'iso' | 'local' | 'utc';
        ms: boolean;
    }
): Promise<void> {
    const service = new UtilsService();

    try {
        if (!input) {
            // Show current timestamp
            const now = new Date();
            const timestamp = service.dateToUnix(now, options.ms);

            console.log('🕒 Current Unix Timestamp:');
            console.log('────────────────────────────────────────');
            console.log(`   ${timestamp}${options.ms ? ' (milliseconds)' : ''}`);
            console.log();
            console.log(`   ISO: ${now.toISOString()}`);
            console.log(`   Local: ${now.toLocaleString()}`);
            console.log();
        } else if (/^\d+$/.test(input)) {
            // Convert unix timestamp to date
            const timestamp = parseInt(input, 10);
            const date = service.unixToDate(timestamp, options.ms);

            console.log('🕒 Unix Timestamp to Date:');
            console.log('────────────────────────────────────────');
            console.log(`   Input: ${timestamp}${options.ms ? ' (ms)' : ''}`);
            console.log();

            if (options.format === 'iso' || options.format === 'utc') {
                console.log(`   ${date.toISOString()}`);
            } else {
                console.log(`   ${date.toLocaleString()}`);
            }
            console.log();
        } else {
            // Convert date string to unix timestamp
            const date = new Date(input);

            if (isNaN(date.getTime())) {
                console.error('❌ Invalid date format');
                process.exit(1);
            }

            const timestamp = service.dateToUnix(date, options.ms);

            console.log('🕒 Date to Unix Timestamp:');
            console.log('────────────────────────────────────────');
            console.log(`   Input: ${input}`);
            console.log(`   Parsed: ${date.toISOString()}`);
            console.log();
            console.log(`   ${timestamp}${options.ms ? ' (ms)' : ''}`);
            console.log();
        }
    } catch (error) {
        console.error('❌ Error:', error instanceof Error ? error.message : String(error));
        process.exit(1);
    }
}

export function createUnixTimeCommand(): Command {
    const cmd = new Command('unix-time')
        .description('Convert unix timestamps to/from dates')
        .argument('[input]', 'Unix timestamp or date string')
        .option('-f, --format <format>', 'Output format (iso, local, utc)', 'iso')
        .option('--ms', 'Use milliseconds instead of seconds')
        .addHelpText('after', `
Examples:
  $ jai1 utils unix-time
  $ jai1 utils unix-time 1702550400
  $ jai1 utils unix-time 1702550400000 --ms
  $ jai1 utils unix-time "2024-01-15 10:30:00"
  $ jai1 utils unix-time "2024-01-15" --format local
  $ jai1 utils unix-time --ms
`)
        .action(async (input, options) => {
            await handleUnixTime(input, options);
        });

    return cmd;
}
